import React, { useState, useEffect, useMemo } from 'react';
import { Alert, Box, LinearProgress } from '@mui/material';
import { EmojiEvents as EmojiEventsIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { IndividualLeaderboardEntry } from '../../models/types';
import { apiService } from '../../services/api.service';
import { EmptyState } from '../ui/EmptyState';
import { LeaderboardTable, LeaderboardRow } from './LeaderboardTable';
import { formatHandle } from '../ui/uiHelpers';

interface IndividualLeaderboardProps {
  contestId: string;
  currentUserId?: string | null;
  // Bumped by the page after changes that affect standings, e.g. an activity sync.
  refreshKey?: number;
  // Row click; receives the shape ParticipantActivityDetail expects.
  onParticipantSelect?: (participant: { userId: string; name: string; username: string; stravaProfile?: any }) => void;
}

interface Participant {
  userId: string;
  name: string;
  username: string;
  stravaProfile?: any;
}

export const IndividualLeaderboard: React.FC<IndividualLeaderboardProps> = ({ contestId, currentUserId, refreshKey, onParticipantSelect }) => {
  const { t } = useTranslation('contests');
  const [leaderboard, setLeaderboard] = useState<IndividualLeaderboardEntry[]>([]);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const [leaderboardData, participantsData] = await Promise.all([
          apiService.getIndividualContestLeaderboard(contestId).catch(() => []),
          apiService.getContestParticipants(contestId).catch(() => []),
        ]);
        setLeaderboard(leaderboardData);
        setParticipants(participantsData);
      } catch (err) {
        console.error('Failed to fetch leaderboard data:', err);
        setError(err instanceof Error ? err.message : t('leaderboard.loadError'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [contestId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Everyone with activities, plus participants who have none yet as zero rows.
  const rows = useMemo<LeaderboardRow[]>(() => {
    const withActivity = new Set(leaderboard.map(entry => entry.userId.toString()));
    const zeroEntries = participants
      .filter(p => !withActivity.has(p.userId.toString()))
      .map(
        p =>
          ({
            userId: p.userId,
            totalDistance: 0,
            totalTracklog: 0,
            averagePace: 0,
            fastestPace: 0,
            maxDistance: 0,
            userDetails: { name: p.name, username: p.username, stravaProfile: p.stravaProfile },
          } as IndividualLeaderboardEntry)
      );

    return [...leaderboard, ...zeroEntries]
      .sort((a, b) => b.totalDistance - a.totalDistance || b.totalTracklog - a.totalTracklog)
      .map((entry, index) => ({
        key: entry.userId,
        rank: index + 1,
        name: entry.userDetails?.name || entry.userId,
        subtitle: formatHandle(entry.userDetails?.username),
        distance: entry.totalDistance,
        count: entry.totalTracklog,
        averagePace: entry.averagePace,
        fastestPace: entry.fastestPace,
        suspiciousCount: entry.suspiciousActivityCount,
        isCurrentUser: !!currentUserId && entry.userId.toString() === currentUserId,
      }));
  }, [leaderboard, participants, currentUserId]);

  if (loading) return <LinearProgress />;
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }
  if (rows.length === 0) {
    return <EmptyState icon={<EmojiEventsIcon />} title={t('leaderboard.emptyTitle')} description={t('leaderboard.emptyDescription')} />;
  }

  const handleRowClick = (row: LeaderboardRow) => {
    const entry = leaderboard.find(e => e.userId === row.key);
    const participant = participants.find(p => p.userId === row.key);
    onParticipantSelect?.({
      userId: row.key,
      name: row.name,
      username: entry?.userDetails?.username || participant?.username || '',
      stravaProfile: entry?.userDetails?.stravaProfile || participant?.stravaProfile,
    });
  };

  // "Active days" mirrors the backend's distinct-day tracklog count.
  return <LeaderboardTable rows={rows} countLabel={t('leaderboard.columns.activeDays')} onRowClick={onParticipantSelect ? handleRowClick : undefined} />;
};
